import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { useGlobalization } from '@Translations/useGlobalization';
import { TFunc } from '@Translations/types';
import { BreakdownComponent } from '@Types/riskBreakdown.types';
import { coverageBand } from '@Components/RiskDetails/Datagrid/RenderCell/coverageBand';
import { Coverage, ReconciledRisk, defaultNote } from './explanation.utils';

// The Minimal-coverage qualifier lives in CoverageQualifier, which the panel
// renders above everything else (spec §5).
export interface CoverageSummaryProps {
	coverage?: Coverage;
	defaulted: BreakdownComponent[];
	risk: ReconciledRisk;
}

const n3 = (v: number | null) => (v === null ? '—' : v.toFixed(3));

// One full sentence per operator and outcome, so translators get whole
// sentences rather than fragments joined in English word order.
const riskEquation = (risk: ReconciledRisk, t: TFunc): string => {
	const vals = {
		lof: n3(risk.lof),
		cof: n3(risk.cof),
		computed: n3(risk.computed),
	};
	const multiplied = risk.method === 'multiplied';
	if (risk.outcome === 'mismatch')
		return multiplied
			? t(
					// eslint-disable-next-line quotes -- apostrophe; prettier requires double quotes here
					"LoF {{lof}} × CoF {{cof}} = {{computed}}, which doesn't match the row's Risk. The model may have changed since this run.",
					vals,
			  )
			: t(
					// eslint-disable-next-line quotes -- apostrophe; prettier requires double quotes here
					"LoF {{lof}} + CoF {{cof}} = {{computed}}, which doesn't match the row's Risk. The model may have changed since this run.",
					vals,
			  );
	if (risk.outcome === 'rescaled')
		return multiplied
			? t(
					'LoF {{lof}} × CoF {{cof}} = {{computed}}, rescaled (this model normalises risk)',
					vals,
			  )
			: t(
					'LoF {{lof}} + CoF {{cof}} = {{computed}}, rescaled (this model normalises risk)',
					vals,
			  );
	if (n3(risk.computed) !== n3(risk.rowRisk))
		return multiplied
			? t('LoF {{lof}} × CoF {{cof}} = {{computed}}', vals)
			: t('LoF {{lof}} + CoF {{cof}} = {{computed}}', vals);
	return multiplied
		? t('LoF {{lof}} × CoF {{cof}}', vals)
		: t('LoF {{lof}} + CoF {{cof}}', vals);
};

export const CoverageSummary = ({
	coverage,
	defaulted,
	risk,
}: CoverageSummaryProps) => {
	const { t } = useGlobalization();
	const band = coverage ? coverageBand(coverage.pct, t) : undefined;

	// Big number: the grid's Risk, whenever the row has one. Muted line: the
	// operands, plus the engine's result whenever it doesn't print the same.
	const noRisk = risk.outcome === 'noRisk' || risk.computed === null;
	const riskLine = noRisk ? '' : riskEquation(risk, t);
	const noteText = {
		lofMissing: t('No LoF — risk is CoF alone.'),
		cofMissing: t('No CoF — risk is LoF alone.'),
		lofZeroAsOne: t('LoF is 0; a multiplied model counts it as 1.'),
		cofZeroAsOne: t('CoF is 0; a multiplied model counts it as 1.'),
	};

	return (
		<Box sx={{ flex: 1, minWidth: 240 }} data-cy="explanation-coverage">
			{coverage && band && (
				<>
					<Typography
						sx={{
							fontSize: 13,
							fontWeight: 700,
							color: '#0d1520',
							mb: 1.5,
						}}>
						{t('Data coverage')}
					</Typography>
					<Tooltip
						title={t(
							'{{real}} of {{total}} inputs came from this asset’s own data. Reflects the last run.',
							{ real: coverage.real, total: coverage.total },
						)}>
						<Typography
							variant="body2"
							data-cy="explanation-coverage-band"
							tabIndex={0}
							sx={{
								display: 'inline-block',
								color: band.color,
								fontWeight: 700,
								textTransform: 'uppercase',
								mb: 0.5,
							}}>
							{t('{{band}} · {{pct}}%', {
								band: band.label,
								pct: coverage.pct,
							})}
						</Typography>
					</Tooltip>
					{defaulted.length > 0 ? (
						<Box
							component="ul"
							sx={{ listStyle: 'none', p: 0, mt: 0.5, mb: 2 }}>
							{defaulted.map((d, i) => {
								const note = defaultNote(d, t);
								return (
									<Typography
										component="li"
										key={`${d.name}-${i}`}
										variant="body2"
										sx={{
											fontSize: '0.8125rem',
											color: '#8a6d1b',
											mb: 0.5,
										}}>
										<b>{d.name}</b>
										{note && ` — ${note}`}
									</Typography>
								);
							})}
						</Box>
					) : (
						<Typography
							variant="body2"
							sx={{ color: 'text.secondary', mb: 2 }}>
							{/* State the coverage fact and stop: complete inputs say nothing
							    about whether the weights or thresholds are right. */}
							{t(
								'Every component was scored from this asset’s own data — no defaults used.',
							)}
						</Typography>
					)}
				</>
			)}

			<Typography
				variant="caption"
				sx={{
					display: 'block',
					color: 'text.secondary',
					textTransform: 'uppercase',
					letterSpacing: '0.05em',
					fontWeight: 700,
				}}>
				{t('Risk Score')}
			</Typography>
			<Box data-cy="explanation-risk">
				<Typography
					sx={{ fontSize: 22, fontWeight: 700, lineHeight: 1.2 }}>
					{n3(risk.rowRisk)}
				</Typography>
				{(!noRisk || risk.rowRisk === null) && (
					<Typography
						variant="body2"
						sx={{ color: 'text.secondary' }}>
						{noRisk ? t('No risk score') : riskLine}
					</Typography>
				)}
			</Box>
			{risk.notes.map(n => (
				<Typography
					key={n}
					variant="caption"
					sx={{ display: 'block', color: 'text.secondary' }}>
					{noteText[n]}
				</Typography>
			))}
		</Box>
	);
};

export default CoverageSummary;
